import React, { useMemo } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useLightMode } from '@/hooks/useLightMode'
import { useTranslation } from 'react-i18next'

import {
  Box,
  ReasoningTrailCard as DarkReasoningTrailCard,
  type ReasoningTrailStep,
} from '@hivewatch/dark'

import { useIncidentReasoningTrailQuery } from '@/graphql/generated/hooks'
import type { AiOperatorFieldSourceType } from '@/graphql/generated/schemas'
import { useAppDispatch } from '@/redux/hooks'
import {
  closeIncidentMapPanelDrawer,
  closeSOPIncidentDrawer,
  closeWorkflowDrawer,
  openTraceLogDrawerForDevice,
} from '@/redux/ui/uiSlice'
import {
  REASONING_TRAIL_ACCESS_LOGS_LINK,
  REASONING_TRAIL_CRITERION_TOGGLE,
  trackEvent,
} from '@/utils/analytics'

import type {
  ReasoningStep,
  ReasoningTrailCardIProps,
} from '../../../../shared/types/reasoningTrailTypes'
import { mapReasoningTrail } from '../../../../shared/utils/mapReasoningTrail'
import { AioFeedback } from './AioFeedback'

/**
 * Incident-drawer container for the AI Operator Reasoning Trail. Fetches the
 * server-computed `Incident.aiOperatorReasoningTrail` on its own, gates on the
 * feature flag, maps it to presentation props, and renders the `@hivewatch/dark`
 * ReasoningTrailCard (under the drawer's root DarkProvider). Does not mount when the flag is off
 * or the trail is null (BE returns null for flag-off / dry-run / no-evaluations).
 */
export const ReasoningTrailCard = ({
  incidentId,
  wasProcessedByAiOperator,
}: ReasoningTrailCardIProps) => {
  const { t } = useTranslation()
  const isEnabled = useFeatureFlag(FeatureFlag.aiOperatorReasoningTrail)
  const colorScheme = useLightMode()
  const dispatch = useAppDispatch()

  // The trail is finalised once the AI produces it, so we fetch it once (kept
  // out of the polled Incident query). We skip entirely until the incident is
  // AI-processed; the Incident query's own polling flips `wasProcessedByAiOperator`
  // when processing completes, which unblocks this fetch — no polling needed here.
  // Query errors are reported to Sentry by the api-general Apollo error link
  // (config/apollo/logger.ts); on failure the card intentionally degrades to
  // absent — the trail is supplementary AI content, not primary incident data.
  const { data, refetch } = useIncidentReasoningTrailQuery({
    variables: { id: incidentId ?? '' },
    skip: !isEnabled || !incidentId || !wasProcessedByAiOperator,
    errorPolicy: 'all',
  })

  // Guard against Apollo cache-and-network races: if the cached data belongs to
  // a different incident (previous drawer selection), treat it as absent so we
  // don't briefly render the old trail while the new fetch is in-flight.
  const rawIncident = data?.incident
  const isStale = rawIncident?.id != null && rawIncident.id !== incidentId
  const trail = isStale ? undefined : rawIncident?.aiOperatorReasoningTrail
  const latestFeedback = isStale
    ? undefined
    : rawIncident?.aiOperatorLatestFeedback

  // The dark card's callbacks give back only the presentation step (id =
  // fieldId). Keep lookups to the raw server step so we can (a) deep-link to the
  // device + qualifying event (only ACS steps carry a link) and (b) recover the
  // `sourceType` the mapper drops, for analytics.
  const traceLogLinkByFieldId = useMemo(() => {
    const map = new Map<string, NonNullable<ReasoningStep['traceLogLink']>>()
    trail?.steps.forEach((step) => {
      if (step.traceLogLink) {
        map.set(step.fieldId, step.traceLogLink)
      }
    })
    return map
  }, [trail])

  const sourceTypeByFieldId = useMemo(() => {
    const map = new Map<string, AiOperatorFieldSourceType>()
    trail?.steps.forEach((step) => {
      map.set(step.fieldId, step.sourceType)
    })
    return map
  }, [trail])

  if (!isEnabled || !trail) {
    return null
  }

  const { steps, outcome } = mapReasoningTrail(trail)
  const outcomeKind = trail.outcome.kind

  // Emit an engagement event on each per-criterion expand/collapse
  const handleToggleCriterion = (
    step: ReasoningTrailStep,
    isExpanded: boolean
  ) => {
    trackEvent(REASONING_TRAIL_CRITERION_TOGGLE, {
      incident_id: incidentId,
      field_id: step.id,
      source_type: sourceTypeByFieldId.get(step.id),
      result: step.result,
      is_decisive: step.isDecisive,
      is_expanded: isExpanded,
      outcome_kind: outcomeKind,
    })
  }

  // Reuse the same trace-log deep-link the ACS EVENT_REF resolution-note pills
  // use: close overlapping drawers, then open the Trace Logs drawer filtered to
  // the step's device (on the Device tab), highlighting the qualifying event.
  const handleViewAccessLogs = (step: ReasoningTrailStep) => {
    const link = traceLogLinkByFieldId.get(step.id)
    if (!link) {
      return
    }
    trackEvent(REASONING_TRAIL_ACCESS_LOGS_LINK, {
      incident_id: incidentId,
      field_id: step.id,
      device_id: link.deviceId,
      has_highlighted_event: Boolean(link.highlightedEvent),
      source_type: sourceTypeByFieldId.get(step.id),
      outcome_kind: outcomeKind,
    })
    const highlightedEvent = link.highlightedEvent
      ? {
          detectedAt: link.highlightedEvent.detectedAt,
          externalId: link.highlightedEvent.externalId,
        }
      : null
    dispatch(closeSOPIncidentDrawer())
    dispatch(closeWorkflowDrawer())
    dispatch(closeIncidentMapPanelDrawer())
    dispatch(
      openTraceLogDrawerForDevice({ deviceId: link.deviceId, highlightedEvent })
    )
  }

  return (
    <Box marginTop={4}>
      <DarkReasoningTrailCard
        colorScheme={colorScheme}
        footer={
          <AioFeedback
            colorScheme={colorScheme}
            incidentId={incidentId ?? ''}
            initialIsPositive={latestFeedback?.isPositive ?? null}
            onSubmitted={refetch}
            outcomeKind={outcomeKind}
          />
        }
        onToggle={handleToggleCriterion}
        onViewAccessLogs={handleViewAccessLogs}
        outcome={outcome}
        reviewedStepsLabel={t('ai_operator:reasoning_trail_reviewed_steps')}
        steps={steps}
      />
    </Box>
  )
}
